import { FormControl } from "./FormControl";
import { ValidationIssue } from "../../rule-engine-core/Common/IDesignValidationProvider";
import { validateVariableName } from "../../rule-engine-core/Common/DataValidationUtilities";
import React from "react";
import { PropertyEditorText } from "../../CommonUI/PropertyEditing/PropertyEditorText";
import { PropertyEditorBoolean } from "../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import VariablePicker from "../../CommonUI/VariablePicker/VariablePicker";
import { DataDefinition } from "../../rule-engine-core/Common/Data/DataDefinition";
import { IDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import styled from "styled-components";
import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { UIToggleButtons } from "../../CommonUI/BuildlingBlocks/UIToggleButtons";
import { ObserveState } from "../../CommonUI/StateManagement/ObserveState";
import { PropertyEditorCollapse } from "../../CommonUI/PropertyEditing/PropertyEditorCollapse";
import { UISpacedBoxContent } from "../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

export abstract class DataFormControl extends FormControl {
    label: string = "Configure Label";
    valueName?: string;
    requiredOnAllOutcomes: boolean = true;
    isPick: boolean = true;
    requiredMessage: string = "value is required";

    getDesignValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        if (!this.valueName) {
            issues.push(new ValidationIssue("Value name is required", undefined, this.id));
        } else {
            let varIssue = validateVariableName(this.valueName);

            if (varIssue) {
                varIssue.dataName = this.valueName;
                varIssue.elementId = this.id;
                issues.push(varIssue);
            }
        }

        return issues;
    }

    getRuntimeValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        if (
            this.requiredOnAllOutcomes &&
            this.valueName &&
            this.getFormContext()?.getFormDataValue(this.valueName) === undefined
        ) {
            issues.push(new ValidationIssue(this.requiredMessage, this.valueName, this.id));
        }

        return issues;
    }

    setIsPick(isPick: boolean): void {
        this.isPick = isPick;
        StateManager.propertyChanged(this, "isPick");
    }

    // produces part of the editor for label and name
    renderBaseDataControlEditor(hideElement?: string[]) {
        return (
            <UISpacedBoxContent>
                {(!hideElement || hideElement.indexOf("label") === -1) && (
                    <PropertyEditorText
                        editObject={this}
                        label={"Label"}
                        propertyName={"label"}
                    />
                )}
                {(!hideElement || hideElement.indexOf("valueName") === -1) && (
                    <UISpacedBoxContent>
                        <UIToggleButtons
                            options={["Pick Data", "New Data"]}
                            value={this.isPick ? "Pick Data" : "New Data"}
                            onChange={(v) => {
                                this.setIsPick(v === "Pick Data");
                            }}
                        />

                        <ObserveState
                            listenTo={this}
                            properties={["isPick"]}
                            control={() => {
                                return (
                                    <UISpacedBoxContent>
                                        {this.isPick && (
                                            <VariablePicker
                                                selected={this.valueName}
                                                data={
                                                    this.getFormContext()?.form.getInputDefinition() ||
                                                    new DataDefinition()
                                                }
                                                isList={false}
                                                onVariableSet={(path: string, def?: IDataDefinition) => {
                                                    if (path || def) {
                                                        if (def) {
                                                            this.getFormContext()
                                                                ?.form.getInputDefinition()
                                                                .fields.push(def!);
                                                        }
                                                        if (path) {
                                                            this.valueName = path;
                                                        } else {
                                                            this.valueName = def!.name;
                                                        }
                                                        StateManager.changed(this);
                                                    }
                                                }}
                                                label={"Value Name"}
                                                variant={"outlined"}
                                                size={"small"}
                                                fullWidth={true}
                                                error={!this.valueName ? "valueName is required" : undefined}
                                            />
                                        )}
                                        {!this.isPick && (
                                            <UISpacedBoxContent>
                                                <PropertyEditorText
                                                    editObject={this}
                                                    label={"Value Name"}
                                                    propertyName={"valueName"}
                                                />
                                                {!this.valueName && (
                                                    <ValidationMessage data-name="valueName">
                                                        valueName is required
                                                    </ValidationMessage>
                                                )}
                                            </UISpacedBoxContent>
                                        )}
                                    </UISpacedBoxContent>
                                );
                            }}
                        />
                    </UISpacedBoxContent>
                )}
                {(!hideElement || hideElement.indexOf("requiredOnAllOutcomes") === -1) && (
                    <UISpacedBoxContent>
                        <PropertyEditorBoolean
                            editObject={this}
                            label={"Required On All Outcomes"}
                            propertyName={"requiredOnAllOutcomes"}
                        />
                        <PropertyEditorCollapse
                            editObject={this}
                            collapseLogicOrField={"requiredOnAllOutcomes"}
                        >
                            <PropertyEditorText
                                editObject={this}
                                label={"Required Message"}
                                propertyName={"requiredMessage"}
                            />
                        </PropertyEditorCollapse>
                    </UISpacedBoxContent>
                )}
            </UISpacedBoxContent>
        );
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(
            this,
            retData,
            "label",
            "valueName",
            "requiredOnAllOutcomes",
            "isPick",
            "requiredMessage",
        );

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(
            this,
            data,
            "label",
            "valueName",
            "requiredOnAllOutcomes",
            "isPick",
            "requiredMessage",
        );
    }
}

const ValidationMessage = styled.div`
    font-size: 12px;
    color: ${UIStyleConstants.RED_COLOR};
`;
